import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, Observable } from 'rxjs';

import { Action, PermissionMatrix, permKey, ProjectRole, ProjectSection } from '../models/access.models';
import { accessFeature } from '../store/access.reducer';
import { AccessActions } from '../store/access.actions';

/**
 * Фасад слоя прав (F1). Единственная точка проверки `can(section, action)` в UI.
 * ВАЖНО: это только UX — реальный enforcement на бэке. Матрица грузится при входе
 * в проект (`loadForProject`).
 */
@Injectable({ providedIn: 'root' })
export class AccessService {
  /** Последний снапшот матрицы — для синхронного `can()`. */
  private _matrix: PermissionMatrix = {};

  readonly matrix$: Observable<PermissionMatrix> = this._store.select(accessFeature.selectMatrix);
  readonly role$: Observable<ProjectRole | null> = this._store.select(accessFeature.selectRole);
  readonly loaded$: Observable<boolean> = this._store.select(accessFeature.selectLoaded);
  readonly projectId$: Observable<number | null> = this._store.select(accessFeature.selectProjectId);

  constructor(private readonly _store: Store) {
    this.matrix$.subscribe((m) => (this._matrix = m ?? {}));
  }

  /** Запустить загрузку матрицы прав для проекта (идемпотентно — стор сам решает). */
  loadForProject(projectId: number): void {
    this._store.dispatch(AccessActions.loadPermissions({ projectId }));
  }

  /** Сбросить права (выход из проекта / логаут). */
  clear(): void {
    this._store.dispatch(AccessActions.clearPermissions());
  }

  /** Синхронная проверка права (по последнему снапшоту матрицы). */
  can(section: ProjectSection, action: Action): boolean {
    return this._matrix[permKey(section, action)] === true;
  }

  /** Реактивная проверка права. */
  can$(section: ProjectSection, action: Action): Observable<boolean> {
    return this.matrix$.pipe(
      map((m) => (m ? m[permKey(section, action)] === true : false)),
      distinctUntilChanged(),
    );
  }

  /**
   * Принимает строку `"section:action"` ИЛИ `"section.action"` (для директивы `*hasPerm`).
   * Возвращает реактивный поток.
   */
  canExpr$(expr: string): Observable<boolean> {
    const [section, action] = expr.split(/[:.]/) as [ProjectSection, Action];
    return this.can$(section, action);
  }
}
